import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { AlertTriangleIcon } from 'lucide-react';

/**
 * A problem triangle that explains itself on hover AND on keyboard focus.
 *
 * A native `title=` needs about a second of stationary hover and never fires on focus, so a
 * Tessra tooltip (`delayDuration = 0`) carries the message instead.
 *
 * `onMouseDown` is prevented, and that is the load-bearing line for the schema grid. There
 * this button sits inside a cell whose own mousedown selects it and returns focus to the grid
 * container, which is where the arrow keys are handled; letting a click land focus here would
 * leave the grid unnavigable until the user tabbed away. Elsewhere it only keeps focus where
 * it was, and the icon stays reachable by Tab.
 *
 * Touch is not covered: Radix tooltips are pointer and keyboard only, with no tap-to-reveal.
 * In the schema grid the details popover's problem list is the touch path; in the list sheet
 * only its count line is.
 */
export function ProblemTriangle({ message, warning }: { message: string; warning: boolean }) {
  return (
    /*
     * Its own provider rather than the page's: Radix throws instead of degrading when one is
     * missing, and this app mounts providers per feature, the same reason `PageHeader` carries
     * its own. Nesting under the page's provider is harmless.
     */
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={message}
            onMouseDown={(event) => event.preventDefault()}
            className="flex shrink-0 items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <AlertTriangleIcon
              className={`size-3.5 ${warning ? 'text-amber-600' : 'text-destructive'}`}
              aria-hidden
            />
          </button>
        </TooltipTrigger>
        <TooltipContent>{message}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
